import { useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Select, Space, Typography } from 'antd';
import { api } from './api';
import { DenseTable as Table, MetricStrip } from './console';
import { valueLabel } from './uiText';

type Balance = { currency: string; income: number; expense: number; balance: number };
type ExpenseGroup = { category: string; payer: string | null; currency: string; amount: number; count: number };
type Detail = ExpenseGroup & { id: string; occurredAt: string; paymentMethod?: string; paymentAccount?: string; counterparty?: string; note?: string };
type Report = { month: string; asOf: string; partial: boolean; balances: Balance[]; categories: string[]; payers: string[];
  totals: { currency: string; amount: number; count: number }[]; breakdown: ExpenseGroup[]; details: Detail[]; total: number; page: number; pageSize: number };

function businessMonth() {
  const date = new Date(Date.now() + 8 * 3600000);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}
const money = (value: number) => value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const payerName = (value: string | null) => value || '未填写';

export function MonthlyReportPage() {
  const [month, setMonth] = useState(businessMonth);
  const [category, setCategory] = useState<string>();
  const [payer, setPayer] = useState<string>();
  const [noteInput, setNoteInput] = useState('');
  const [note, setNote] = useState('');
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<Report>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setData(undefined);
    const query = new URLSearchParams({ month, page: String(page) });
    if (category) query.set('category', category);
    if (note) query.set('note', note);
    if (payer === 'missing') query.set('missingPayer', 'true');
    else if (payer) query.set('payer', payer.slice(5));
    void api<Report>(`/api/v1/bi/monthly?${query}`).then(result => {
      if (active) setData(result);
    }).catch(reason => { if (active) setError(String(reason)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [month, category, payer, note, page, revision]);
  const groupColumns = [
    { title: '支出类别', dataIndex: 'category', render: (value: string) => valueLabel(value) },
    { title: '付款人', dataIndex: 'payer', render: payerName },
    { title: '币种', dataIndex: 'currency' },
    { title: '合计金额', dataIndex: 'amount', render: money },
    { title: '笔数', dataIndex: 'count' },
  ];
  return <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
    <Card><Space wrap>
      <Typography.Text strong>报告月份</Typography.Text>
      <Input aria-label="报告月份" type="month" min="2000-01" max={businessMonth()} value={month} style={{ width: 175 }} onChange={event => {
        const next = event.target.value;
        if (/^\d{4}-\d{2}$/.test(next) && next >= '2000-01' && next <= businessMonth()) {
          setMonth(next); setCategory(undefined); setPayer(undefined); setPage(1);
        }
      }} />
      <Button onClick={() => setRevision(value => value + 1)} loading={loading}>刷新月报</Button>
      <Typography.Text type="secondary">按北京时间自然月统计；收入包含订单实收及其他收入，并扣除冲销，作废支出不计入，币种分别汇总。</Typography.Text>
    </Space></Card>
    {error && <Alert type="error" showIcon title="月报加载失败" description={error} />}
    <Card title={`${month} 月度收支报告`} loading={loading}>
      {data && <>
        <Typography.Paragraph type="secondary">{data.partial ? '本月进行中' : '完整自然月'} · 数据截至 {new Date(data.asOf).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })}（北京时间）</Typography.Paragraph>
        {data.balances.length ? data.balances.map(row => <MetricStrip key={row.currency} items={[
          { label: `收入 · ${row.currency}`, value: row.income, precision: 2 },
          { label: `支出 · ${row.currency}`, value: row.expense, precision: 2 },
          { label: `收支结余 · ${row.currency}`, value: row.balance, precision: 2 },
        ]} />) : <Alert type="info" title="该月暂无收支记录" />}
      </>}
    </Card>
    <Card title="支出分析">
      <Space wrap style={{ marginBottom: 16 }}>
        <Select aria-label="支出类别" placeholder="全部支出类别" allowClear showSearch style={{ width: 200 }} value={category}
          optionFilterProp="label" options={data?.categories.map(value => ({ value, label: valueLabel(value) }))}
          onChange={value => { setCategory(value); setPage(1); }} />
        <Select aria-label="付款人" placeholder="全部付款人" allowClear showSearch style={{ width: 200 }} value={payer}
          optionFilterProp="label" options={[{ value: 'missing', label: '未填写付款人' }, ...(data?.payers ?? []).map(value => ({ value: `name:${value}`, label: value }))]}
          onChange={value => { setPayer(value); setPage(1); }} />
        <Input.Search aria-label="备注关键词" placeholder="备注关键词，如大哥、老弟、212" maxLength={256} value={noteInput} style={{ width: 290 }} enterButton="查询"
          onChange={event => setNoteInput(event.target.value)} onSearch={value => { setNote(value.trim()); setPage(1); }} />
        <Button onClick={() => { setCategory(undefined); setPayer(undefined); setNote(''); setNoteInput(''); setPage(1); }}>清除筛选</Button>
      </Space>
      <Typography.Paragraph type="secondary">可组合查询，例如类别“电费”＋备注“大哥”。备注仅按文字匹配，不自动认定付款人。付款人未录入时显示“未填写”。以下合计覆盖全部匹配记录{note ? `；当前备注关键词：${note}` : ''}。</Typography.Paragraph>
      {data?.totals.map(row => <Typography.Paragraph key={row.currency}><strong>{row.currency} 合计 {money(row.amount)}</strong> · {row.count} 笔</Typography.Paragraph>)}
      <Table<ExpenseGroup> loading={loading} rowKey={row => JSON.stringify([row.category, row.payer, row.currency])}
        columns={groupColumns} dataSource={data?.breakdown ?? []} pagination={{ pageSize: 10 }} scroll={{ x: 650 }} locale={{ emptyText: '没有匹配的支出' }} />
    </Card>
    <Card title="支出明细">
      <Table<Detail> loading={loading} rowKey="id" dataSource={data?.details ?? []} scroll={{ x: 1100 }}
        pagination={{ current: data?.page ?? page, pageSize: 50, total: data?.total ?? 0, showSizeChanger: false, onChange: setPage, showTotal: total => `共 ${total} 笔` }}
        columns={[
          { title: '时间（北京时间）', dataIndex: 'occurredAt', render: (value: string) => new Date(value).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' }) },
          ...groupColumns.slice(0, 3), { title: '金额', dataIndex: 'amount', render: money },
          { title: '支付方式', dataIndex: 'paymentMethod' }, { title: '支付账户', dataIndex: 'paymentAccount' },
          { title: '收款方', dataIndex: 'counterparty' }, { title: '备注', dataIndex: 'note' },
        ]} locale={{ emptyText: '没有匹配的支出明细' }} />
    </Card>
  </Space>;
}
